import { toast } from 'sonner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { RouterLink } from '@/components/router/router-link';
import { authControllerForgotPassword } from '@/apis/backend/generated/auth/auth';

import { MailOutline } from '@mui/icons-material';
import {
  Box,
  Link,
  Stack,
  Button,
  TextField,
  Typography,
} from '@mui/material';

import { paths } from 'src/routes/paths';

export const Route = createFileRoute('/auth/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error(t('toasts.enterEmail'));
      return;
    }

    setLoading(true);

    try {
      await authControllerForgotPassword({ email });

      setSent(true);
    } catch (err: any) {
      console.error('Reset password error:', err);
      const message = err?.message || t('forgotPassword.error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setCooldown(true);
    await handleSendLink({ preventDefault: () => {} } as React.FormEvent);

    setTimeout(() => {
      setCooldown(false);
    }, 60000);
  };

  if (sent) {
    return (
      <Box sx={{ textAlign: 'center' }}>
          <MailOutline sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />

          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {t('forgotPassword.checkEmailTitle')}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t('forgotPassword.sentTo')} <strong>{email}</strong>
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('forgotPassword.clickLink')}
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleResend}
              disabled={cooldown || loading}
              sx={{ mb: 1 }}
            >
              {cooldown ? t('forgotPassword.resendCooldown') : t('forgotPassword.resend')}
            </Button>

            <br />

            <Link component={RouterLink} href={paths.auth.signIn} sx={{ cursor: 'pointer' }}>
              <Typography variant="body2">{t('links.backToSignIn')}</Typography>
            </Link>
          </Box>
        </Box>
    );
  }

  return (
    <>
      <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700, letterSpacing: '-0.02em' }}>
        {t('forgotPassword.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('forgotPassword.subtitle')}
      </Typography>

      <Stack component="form" spacing={2.5} onSubmit={handleSendLink}>
        <TextField
          fullWidth
          label={t('fields.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          autoComplete="email"
        />

        <Button fullWidth size="large" type="submit" variant="contained" disabled={loading} sx={{ py: 1.4 }}>
          {loading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
        </Button>
      </Stack>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Link href={paths.auth.signIn} sx={{ cursor: 'pointer' }}>
          <Typography variant="body2">{t('links.backToSignIn')}</Typography>
        </Link>
      </Box>
    </>
  );
}
